import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MarketplaceNFT {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  owner_wallet: string;
  owner_name: string | null;
  owner_id: string | null;
  creator_wallet: string | null;
  creator_name: string | null;
  price: number;
  current_price: number | null;
  sales_count: number;
  total_earned: number;
  created_at: string;
  is_listed: boolean;
  image_verified: boolean | null;
  image_check_status: string | null;
  collection_name: string | null;
}

export interface CollectionInfo {
  collection_name: string;
  nft_count: number;
  total_sales: number;
  total_volume: number;
}

export type SortOption = "trending" | "recent" | "top_emon" | "collection";

export function useMarketplaceNFTs() {
  const [nfts, setNfts] = useState<MarketplaceNFT[]>([]);
  const [filteredNfts, setFilteredNfts] = useState<MarketplaceNFT[]>([]);
  const [collections, setCollections] = useState<CollectionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("trending");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);

  const fetchNFTs = useCallback(async () => {
    try {
      // Include all NFTs (listed and holding) - buy button disabled for holding NFTs
      const { data, error } = await supabase
        .from("nfts")
        .select("id, name, description, image_url, owner_wallet, owner_name, owner_id, creator_wallet, creator_name, price, current_price, sales_count, total_earned, created_at, is_listed, image_verified, image_check_status, collection_name")
        .neq("image_check_status", "rejected")
        .eq("is_wash_trade_detected", false)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching marketplace NFTs:", error);
        return;
      }

      // Note: Shadow-banned filtering is handled server-side via RLS
      // We can't query profiles table from unauthenticated users
      const nftData = (data || []) as MarketplaceNFT[];
      setNfts(nftData);

      // Extract unique collections from NFTs
      const collectionMap = new Map<string, CollectionInfo>();
      nftData.forEach((nft) => {
        if (nft.collection_name) {
          const existing = collectionMap.get(nft.collection_name);
          if (existing) {
            existing.nft_count++;
            existing.total_sales += nft.sales_count;
            existing.total_volume += nft.total_earned;
          } else {
            collectionMap.set(nft.collection_name, {
              collection_name: nft.collection_name,
              nft_count: 1,
              total_sales: nft.sales_count,
              total_volume: nft.total_earned,
            });
          }
        }
      });
      setCollections(Array.from(collectionMap.values()).sort((a, b) => b.total_sales - a.total_sales));
    } catch (err) {
      console.error("Marketplace error:", err);
    }
    setLoading(false);
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...nfts];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (nft) =>
          nft.name.toLowerCase().includes(searchLower) ||
          nft.owner_wallet.toLowerCase().includes(searchLower) ||
          (nft.owner_name?.toLowerCase() || "").includes(searchLower)
      );
    }

    // Collection filter
    if (selectedCollections.length > 0) {
      result = result.filter((nft) => 
        nft.collection_name && selectedCollections.includes(nft.collection_name)
      );
    }

    // Price range filter - prices are already in USD
    result = result.filter((nft) => {
      const priceUsd = nft.current_price || nft.price;
      return priceUsd >= priceRange[0] && priceUsd <= priceRange[1];
    });

    // Sorting
    switch (sortBy) {
      case "trending":
        result.sort((a, b) => {
          const scoreA = a.sales_count * 10 + a.total_earned * 100;
          const scoreB = b.sales_count * 10 + b.total_earned * 100;
          return scoreB - scoreA;
        });
        break;
      case "recent":
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case "top_emon":
        result.sort((a, b) => b.total_earned - a.total_earned);
        break;
      case "collection":
        result.sort((a, b) => {
          const colA = a.collection_name || "";
          const colB = b.collection_name || "";
          return colA.localeCompare(colB);
        });
        break;
    }

    setFilteredNfts(result);
  }, [nfts, search, sortBy, priceRange, selectedCollections]);

  useEffect(() => {
    fetchNFTs();
  }, [fetchNFTs]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("marketplace-nfts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "nfts" },
        () => {
          fetchNFTs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNFTs]);

  return {
    nfts: filteredNfts,
    allNfts: nfts,
    collections,
    loading,
    search,
    setSearch,
    sortBy,
    setSortBy,
    priceRange,
    setPriceRange,
    selectedCollections,
    setSelectedCollections,
    refresh: fetchNFTs,
  };
}
